/**
 * Toast notification store. Manages a queue of toast items with auto-dismiss
 * timers. Rendered by ToastContainer.svelte, which is mounted once in App.svelte.
 *
 * Prefer the helpers in `toast.ts` (toastSuccess / toastError / …) at call sites.
 */

import { writable } from 'svelte/store';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem
{
	id: number;
	type: ToastType;
	message: string;
	detail?: string;
	duration: number;
	createdAt: number;
	/** 0..1. When set, the toast shows a progress bar and does not auto-dismiss until it reaches 1. */
	progress?: number;
}

const MAX_VISIBLE = 5;
const DURATIONS: Record<ToastType, number> = { success: 4000, info: 4000, warning: 5000, error: 7000 };

let nextID = 1;
const timers: Map<number, ReturnType<typeof setTimeout>> = new Map();

function createToastStore()
{
	const { subscribe, update } = writable<ToastItem[]>([]);

	function push(type: ToastType, message: string, detail?: string, duration?: number, progress?: number): number
	{
		const id = nextID++;
		const dur = duration ?? DURATIONS[type];
		const item: ToastItem = { id, type, message, detail, duration: dur, createdAt: Date.now(), progress };

		update((items) =>
		{
			const next = [item, ...items];
			if (next.length > MAX_VISIBLE) next.length = MAX_VISIBLE;
			return next;
		});

		if (item.progress == null)
		{
			const timer = setTimeout(() => dismiss(id), dur);
			timers.set(id, timer);
		}
		return id;
	}

	function setProgress(id: number, progress: number): void
	{
		update((items) => items.map((t) => t.id === id ? { ...t, progress } : t));

		if (progress < 1)
		{
			// Still running — keep the toast pinned open.
			const existing = timers.get(id);
			if (existing)
			{
				clearTimeout(existing);
				timers.delete(id);
			}
		}
		else
		{
			if (!timers.has(id))
			{
				let dur = DURATIONS.success;
				const unsub = subscribe((items) => {
					const item = items.find((t) => t.id === id);
					if (item) dur = item.duration;
				});
				unsub();
				timers.set(id, setTimeout(() => dismiss(id), dur));
			}
		}
	}

	function dismiss(id: number): void
	{
		update((items) => items.filter((t) => t.id !== id));
		const timer = timers.get(id);
		if (timer)
		{
			clearTimeout(timer);
			timers.delete(id);
		}
	}

	return { subscribe, push, setProgress, dismiss };
}

export const toastStore = createToastStore();
